import {RelayPagination, RelayPaginationState, useRelayPagination} from "../../pagination";
import {asQueryParam, ProviderMetricInfo, ProviderMetricsFilterValue} from "./domain";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {EnrichedTable, LoadingSpinner, RelayConnection, SimpleDropdown, SortBy, ErrorDisplay} from "../../../common";
import React, {Dispatch, useEffect} from "react";
import {ProviderMetricsPageAction} from "./ProviderMetricsPage";
import {useLazyQuery$} from "../../../graphqlsupport";
import {SortOrder} from "../../../placement/domains";
import {gql} from "@apollo/client";
import {useObservable} from "../../../rxsupport";
import {Link, Pane} from "evergreen-ui";
import ProviderMetricsFilterBadge from "./ProviderMetricsFilterBadge";
import ProviderMetricsFilter from "./ProviderMetricsFilter";
import {formatDateStr, formatISODateStr} from "../../../common/util/date";
import {ProviderMetricsReportTypeMap} from "../domain";
import {SubmitInquiryModal} from "./SubmitInquiryModal";
import {useSubmitProviderMetricInquiry$} from "./useSubmitProviderMetricsInquiry";
import {linkPrefix} from "../../layouts/util";

export const PROVIDER_METRICS_QUERY = gql`
query providerMetricsQuery($first: Int = 20, $after: String, $params: ProviderMetricParamsInput!) {
    providerMetricsQuery(
        first: $first
        after: $after
        params: $params
    ) {
        edges {
            cursor
            node {
                    guid
                    reportType
                    reportName
                    reportPeriodStart
                    reportPeriodEnd
                    reportAvailableDate
                    documentLink
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }

}
`

type ProviderMetricListProps = {
    pagination: RelayPaginationState;
    filter: ProviderMetricsFilterValue;
    pageSize: number;
    sortBy: SortBy<ProviderMetricInfo>;
    dispatch: Dispatch<ProviderMetricsPageAction>;
    showSubmitInquiryModal: boolean;
}

type ProviderMetricsQueryResponse = RelayConnection<ProviderMetricInfo> & { totalElements: number };

export const ProviderMetricList = ({
                                       pagination,
                                       dispatch,
                                       filter,
                                       sortBy,
                                       showSubmitInquiryModal,
                                       pageSize,
                                   }: ProviderMetricListProps) => {
    const {execute, data, isRunning$, error} = useLazyQuery$<{
        first: number;
        after: string;
        params: {
            reportType: string | null;
            reportPeriod: string | null;
            reportAvailableDateFrom: string | null;
            reportAvailableDateTo: string | null;
            sortDir: SortOrder,
            sortBy: string,
        }
    }, { providerMetricsQuery: ProviderMetricsQueryResponse }>(
        PROVIDER_METRICS_QUERY,
        {
            ...pagination.toGraphQlParams(),
            params: {
                ...asQueryParam(filter),
                sortDir: sortBy.order,
                sortBy: sortBy.column,
            }
        }
    )

    const startFetch = () => {
        execute().then(() => console.debug("Initial fetch"))
    }

    useEffect(() => {
        startFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const loading = useObservable(isRunning$, true);
    const {useSubmitProviderMetricInquiry} = useSubmitProviderMetricInquiry$();
    let totalRows: number = 0;
    if (data?.providerMetricsQuery?.pageInfo?.endCursor !== null) {
        totalRows = data?.providerMetricsQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalRows);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading provider metrics" />;
    }
    const providerMetrics = data?.providerMetricsQuery.edges.map((edge) => edge.node) || [];

    return <Pane className="h-full pt-5 m-5 bg-white shadow-md flex flex-col"
                 minHeight="calc(100vh - 100px - 260px - 40px)">
        <Pane display='flex' justifyContent='space-between' padding={2} paddingBottom={8}>
            <div className={'text-sm pl-4'}>
                <a href={`https://providers${linkPrefix()}.catalight.org/document/provider-performance-data-guide`} target="_blank" rel="noopener noreferrer">
                    <div className='underline text-blue-portal-link'>
                        Provider Data Guide
                    </div>
                </a>
            </div>
            <div className='flex'>
                <div className="pr-1 flex items-center">
                    <FatOutlineButton
                        type={"button"}
                        text={"Submit Provider Data Inquiry"}
                        onClick={() => dispatch({type: "SHOW_SUBMIT_INQUIRY_MODAL"})}
                        color={'maroon'}
                        border={false}
                        minWidth={"14rem"}
                        height={"2.5rem"}
                    />
                </div>
                <ProviderMetricsFilter initFilterValue={filter} dispatch={dispatch}/>
                <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                minWidth={"2rem"}
                                textColor={"gray-700"}
                                setSelected={(newPageSize) => dispatch({
                                    type: "SET_PAGE_SIZE",
                                    pageSize: parseInt(newPageSize)
                                })}/>
                <RelayPagination pageInfo={data?.providerMetricsQuery.pageInfo}
                                 count={totalRows}
                                 onClick={onPaginationClick}/>
            </div>
        </Pane>
        <ProviderMetricsFilterBadge filter={filter} dispatch={dispatch}/>
        <EnrichedTable<ProviderMetricInfo>
            firstColumn={'reportName'}
            sortBy={sortBy}
            styleOverride={{
                customTableStyle: {height: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"},
                tableContent: "overflow-auto flex flex-col flex-grow mb-4 scrollbar-always",
                firstColStyle: "pl-4"
            }}
            columns={[
                {attr: 'reportName', name: 'Report Name', span: 4},
                {attr: 'reportType', name: 'Report Type', span: 2},
                {attr: '_reportPeriod', name: 'Report Period', span: 2},
                {attr: 'reportAvailableDate', name: 'Report Available Date', span: 2},
            ]}
            rowsData={providerMetrics}
            keyColumn={'guid'}
            cellDisplayFunc={(metric, column) => {
                if (column.attr === 'reportAvailableDate') {
                    return formatDateStr(metric.reportAvailableDate);
                }
                if (column.attr === '_reportPeriod') {
                    return formatISODateStr(metric.reportPeriodStart, "MM/yyyy") + " - " + formatISODateStr(metric.reportPeriodEnd, "MM/yyyy");
                }
                if (column.attr === 'reportType') {
                    return ProviderMetricsReportTypeMap.get(metric.reportType) ?? "";
                }

                return <>
                    <Link target="_blank" rel="noopener" href={metric.documentLink}>
                        <span className="text-blue-portal-link">
                            {metric.reportName}
                        </span>
                    </Link>
                </>;
            }}
            emptyMessage={
                <div className='w-full flex items-center'>
                    <div className='m-auto pt-8'>
                        There are no provider metrics
                    </div>
                </div>
            }
            loadingFeat={{
                loading: loading,
                loadingDisplayElement: <LoadingSpinner spacing={20} height={"40vh"}/>
            }}
        />
        <SubmitInquiryModal visible={showSubmitInquiryModal}
                            dispatch={dispatch}
                            onFinished={useSubmitProviderMetricInquiry}/>
    </Pane>
}